/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { animated, useSpring } from '@react-spring/web';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useAccount } from '~/apis';
import { HorseStatistic, rounds, selectHorsesStatistics, useSelector, useTranslation } from '~/app';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/components/Dialog/Dialog';
import Turfmaster from '~/server/src/tmmodel';
import { useOnStateChange } from '~/utils';
import { cn } from '~/utils/styles';

import HorseCell from './HorseCell';
import UserCell from './UserCell';

export type StatisticsModalState = 'closed' | 'timed' | 'open';

interface StatisticsProps {
  state: StatisticsModalState;
  onClose: () => void;
  isFinalRoundTransition?: boolean;
  duration?: number;
}

const COLLAPSED_WIDTH = 60;
const DEFAULT_WIDTH = 200;

const getDisplayedRank = (row: HorseStatistic): string | number => {
  if (row.rank >= 0) return row.rank + 1;
  if (row.totalRank >= 0) return row.totalRank + 1;
  return '-';
};

const getRankSortValue = (row: HorseStatistic): number => {
  if (row.rank >= 0) return row.rank;
  if (row.totalRank >= 0) return row.totalRank;
  return row.turnPos;
};

type StatisticsColumnConfig = {
  id: string;
  header: React.ReactNode;
  widthPx: number;
  alignRight: boolean;
  renderCell: (row: HorseStatistic) => React.ReactNode;
};

const Statistics: React.FC<StatisticsProps> = ({
  state,
  onClose,
  isFinalRoundTransition = false,
  duration = Turfmaster.endRoundTimeout,
}): JSX.Element => {
  const { t } = useTranslation();
  const { data: account } = useAccount();
  const statistics = useSelector(selectHorsesStatistics);
  const isLoading = !statistics;
  const displayStatistics = useMemo(
    () =>
      [...(statistics ?? [])].sort(
        (left, right) => getRankSortValue(left) - getRankSortValue(right),
      ),
    [statistics],
  );

  const [{ width }] = useSpring<{ width: string }>(
    {
      from: { width: '100%' },
      to: { width: state === 'timed' ? '0%' : '100%' },
      config: { duration: state === 'timed' ? duration : 0 },
    },
    [state, duration],
  );
  const [remainingSeconds, setRemainingSeconds] = useState(Math.ceil(duration / 1000));

  useEffect(() => {
    if (state !== 'timed') return;

    const endAt = Date.now() + duration;
    const updateRemainingSeconds = () => {
      const remainingMs = Math.max(endAt - Date.now(), 0);
      setRemainingSeconds(Math.ceil(remainingMs / 1000));
    };

    updateRemainingSeconds();
    const interval = window.setInterval(updateRemainingSeconds, 250);
    return () => window.clearInterval(interval);
  }, [duration, state]);

  const columns = useMemo<StatisticsColumnConfig[]>(
    () => [
      {
        id: 'rank',
        header: '#',
        widthPx: COLLAPSED_WIDTH,
        alignRight: true,
        renderCell: (row) => getDisplayedRank(row),
      },
      {
        id: 'horse',
        header: t('game_statisticsHeaderHorse'),
        widthPx: DEFAULT_WIDTH,
        alignRight: false,
        renderCell: (row) => (
          <HorseCell
            value={{
              color: row.color,
              name: row.name,
              jockeyName: row.jockeyName,
            }}
          />
        ),
      },
      {
        id: 'user',
        header: t('game_statisticsHeaderPlayer'),
        widthPx: DEFAULT_WIDTH,
        alignRight: false,
        renderCell: (row) => (
          <UserCell
            value={{
              country: row.country,
              name: row.playerName,
              userId: row.userId,
            }}
          />
        ),
      },
      ...rounds.map((round) => ({
        id: `round-${round}`,
        header: `${round + 1}`,
        widthPx: COLLAPSED_WIDTH,
        alignRight: true,
        renderCell: (row: HorseStatistic) => (row.points[round] > 0 ? row.points[round] : '-'),
      })),
      {
        id: 'pointsSum',
        header: <span>&#x2211;</span>,
        widthPx: COLLAPSED_WIDTH,
        alignRight: true,
        renderCell: (row) => row.pointsSum,
      },
    ],
    [t],
  );

  useOnStateChange(
    state,
    useCallback(
      (newState) => {
        let timeout: number | undefined;
        if (newState === 'timed') {
          timeout = window.setTimeout(onClose, duration);
        }
        return () => window.clearTimeout(timeout);
      },
      [duration, onClose],
    ),
  );

  const renderTable = useCallback(() => {
    if (!displayStatistics.length) {
      return <div className="p-4 text-center">{t('game_statisticsNoData')}</div>;
    }

    return (
      <table className="w-full border-collapse overflow-y-auto">
        <thead className="border-b border-gray-400">
          <tr>
            {columns.map((col, index) => (
              <th
                key={col.id}
                className={cn('p-1 text-left md:p-2', {
                  'text-center': index === 0 || index > columns.length - 5,
                })}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y text-sm md:text-base lg:text-lg">
          {displayStatistics.map((row, rowIndex) => (
            <tr
              key={`${row.userId ?? 'anon'}-${row.name}-${rowIndex}`}
              className={cn({
                'bg-secondary/75': row.userId === account?.userId,
              })}
            >
              {columns.map((col) => (
                <td
                  key={col.id}
                  className={cn('px-2 py-1 md:px-3 md:py-2', {
                    'text-right': col.alignRight,
                  })}
                  style={{
                    width: `${col.widthPx}px`,
                    minWidth: `${col.widthPx}px`,
                  }}
                >
                  {col.renderCell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, [displayStatistics, columns, account?.userId, t]);

  if (isLoading) {
    return (
      <Dialog open={state !== 'closed'} onOpenChange={() => {}}>
        <DialogContent size="xl" className="pointer-events-auto" showCloseButton={false}>
          <DialogTitle className="sr-only">{t('game_statisticsTitle')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('game_statisticsDescription')}
          </DialogDescription>
          <div className="flex h-32 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={state !== 'closed'} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        size="xl"
        className="pointer-events-auto w-full max-w-4xl"
        showCloseButton={true}
      >
        <div className="relative">
          <DialogTitle className="flex flex-col gap-2 font-bold">
            <span>{t('game_statisticsTitle')}</span>
            {state === 'timed' && (
              <>
                <div className="text-sm font-normal text-gray-700">
                  {t(
                    isFinalRoundTransition
                      ? 'game_statisticsCountdownEndGame'
                      : 'game_statisticsCountdownNextRound',
                    { placeholders: { seconds: remainingSeconds } },
                  )}
                </div>
                <div
                  className="group flex w-full items-center"
                  title={
                    t('game_statisticsTimerTooltip') || 'Dieses Fenster schließt sich automatisch'
                  }
                >
                  <svg
                    className="mr-2"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12" cy="12" r="10" stroke="#00796b" strokeWidth="2" fill="white" />
                    <path
                      d="M12 6v6l4 2"
                      stroke="#00796b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                    <animated.div
                      className="absolute top-0 left-0 h-2 rounded-full bg-linear-to-r from-primary to-teal-200 shadow-md"
                      style={{ width }}
                    />
                  </div>
                </div>
              </>
            )}
          </DialogTitle>
          {renderTable()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default memo(Statistics);
